import React, { useContext, useState, useEffect } from 'react';
import { ICustomAttribute, Order, IGroup, IPolicyAuth } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { MenuItem, Typography, Grid, Box } from '@material-ui/core';
import { IOffice365State } from '..';
import { PasslySelect, PasslyCheckbox } from '@app/components';
import AppsContext from '../appContext';
import { useDebounce, genericErrorToast } from '@app/helpers';
import { groupsService, basicService } from '@app/services';
import { useSelector } from 'react-redux';
import { PasslyAutoComplete } from '@app/componentsV2';
import {
    GROUP_TYPE_SYNC_MODES_BACKER,
    MEMBERSHIP_SYNC_MODES,
    MODULE_NAME,
    SYNC_SOURCES_BACKER
} from '@app/pages/ssoManager/addEditApplication.enums';

interface IProps {
    officeConfig: IOffice365State;
    updateConfig: Function;
    appId: string;
}

export const ConfigureSync = ({ officeConfig, updateConfig }: IProps) => {
    const { translation } = useTypedTranslation();
    const { AttributeRules, updateState } = useContext(AppsContext);
    const organizationFilter = basicService.getCurrentOrgFilter();
    const provisioningPolicies: IPolicyAuth[] = useSelector((state: any) => state.provisioningPolicies.provisioningPoliciesDTO);
    const [groups, setGroups] = useState<IGroup[]>([]);
    const [groupFilterText, setGroupFilterText] = useState('');
    const debouncedSearch = useDebounce(groupFilterText, 500);
    const syncSources = officeConfig.syncSources.length ? officeConfig.syncSources : SYNC_SOURCES_BACKER;
    const groupTypeSyncModes = GROUP_TYPE_SYNC_MODES_BACKER;

    useEffect(() => {
        if (debouncedSearch === '') {
            getGroupsInitial();
        } else {
            groupsService.getGroupsDTOFilter('name', Order.asc, organizationFilter, groupFilterText, genericErrorToast, handleGroupsList);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        if (provisioningPolicies.length > 0) {
            let foundPolicy = false;

            for (let i = 0; i < provisioningPolicies.length; i++) {
                if (provisioningPolicies[i].Id === officeConfig.importedUsersProvisioningPolicyId) {
                    foundPolicy = true;
                    break;
                }
            }

            if (!foundPolicy) {
                updateConfig('importedUsersProvisioningPolicyId', provisioningPolicies[0].Id);
            }
        }
    }, [provisioningPolicies, officeConfig]);

    const handleSyncSourceChange = (name: string, value: string) => {
        let attributeRules = JSON.parse(AttributeRules);

        attributeRules.Entries?.forEach(function (e: ICustomAttribute) {
            if (e.Type === 'http://schemas.xmlsoap.org/claims/UPN') {
                if (value === 'EmailAddress') {
                    e.Emit = '{User.EmailAddress}';
                }

                if (value === 'PrincipalName') {
                    e.Emit = '{User.PrincipalName}';
                }

                if (value === 'PrincipalNameWithSuffix') {
                    e.Emit = '{User.PrincipalName}{Organization.PrincipalNameSuffix}';
                }

                updateState('AttributeRules', JSON.stringify(attributeRules));
            }
        });

        updateConfig(name, value);
    };

    const handleGroupTypeSyncModeChange = (name: string, value: string) => {
        updateConfig(name, value);
    };

    const getGroupsInitial = () => {
        groupsService.getAllGroupsDTOInitial(Order.asc, organizationFilter).then(handleGroupsList).catch(genericErrorToast);
    };

    const handleGroupsList = (response: any) => {
        const results: IGroup[] = response;

        if (results) {
            setGroups(results);
        }
    };

    const handleCheckboxChange = (checked: boolean, event: any) => {
        updateConfig(event.target.name, checked);
    };

    const handleSelectSyncSource = (e: any) => handleSyncSourceChange(e.target.name || '', e.target.value as string);
    const handleSelectGroupTypeSyncMode = (e: any) => handleGroupTypeSyncModeChange(e.target.name || '', e.target.value as string);
    const handleConfigUpdate = (e: any) => updateConfig(e.target.name, e.target.value);
    const getSyncSourceTemplate = (item: any) => {
        return (
            <MenuItem value={item.value} disabled={item.disabled}>
                {item.label}
            </MenuItem>
        );
    };
    const handleAutocompleteChange = (e: any) => {
        const chosen = e.target.value;

        updateConfig('importedUsersInitialGroupId', chosen?.Id);
    };

    return (
        <Grid container justifyContent='space-between'>
            <Grid item lg={5} sm={7}>
                <Typography variant='h6'>{translation('MSOL_CONFIGURE_SYNC')}</Typography>
                <div className='msol__sub-title'>{translation('MSOL_SYNC_ALWAYS_ENABLED')}</div>

                <PasslyCheckbox
                    name='syncGroupsEnabled'
                    checked={officeConfig.syncGroupsEnabled}
                    onChange={handleCheckboxChange}
                    label={translation('MSOL_ENABLE_SYNC_GROUPS')}
                />
                {officeConfig.syncGroupsEnabled && (
                    <Box mb={2}>
                        <Box>
                            <PasslySelect
                                fullWidth
                                name='selectedGroupTypeSyncMode'
                                value={officeConfig.selectedGroupTypeSyncMode}
                                id='selectedGroupTypeSyncMode-input'
                                onChange={handleSelectGroupTypeSyncMode}
                                label={'MSOL_GROUP_TYPE_SYNC_MODE'}
                                options={groupTypeSyncModes}
                            />
                        </Box>
                        {officeConfig.selectedGroupTypeSyncMode === 'IfTypeMatches' ? (
                            <PasslyCheckbox
                                name='forceNewGroupCreation'
                                checked={officeConfig.forceNewGroupCreation}
                                onChange={handleCheckboxChange}
                                label={translation('MSOL_FORCE_CREATE_GROUP')}
                            />
                        ) : null}
                        <PasslyCheckbox
                            name='removeGroupDuplicates'
                            checked={officeConfig.removeGroupDuplicates}
                            onChange={handleCheckboxChange}
                            label={translation('MSOL_REMOVE_GROUP_DUPLICATES')}
                        />
                        <div className='msol__sub-title'>{translation('MSOL_MEMBERSHIP_EXPORT_TITLE')}</div>

                        <Box mb={2}>
                            <PasslySelect
                                className='msol__select'
                                fullWidth
                                name='membershipSyncMode'
                                value={officeConfig.membershipSyncMode}
                                id='membershipSyncMode-input'
                                onChange={handleConfigUpdate}
                                label={'MSOL_MEMBERSHIP_EXPORT_LABEL'}
                                options={MEMBERSHIP_SYNC_MODES}
                            />
                        </Box>
                    </Box>
                )}

                <Box mb={2}>
                    <PasslySelect
                        className='msol__select'
                        fullWidth
                        name='selectedSyncSource'
                        value={officeConfig.selectedSyncSource}
                        id='syncSources-input'
                        onChange={handleSelectSyncSource}
                        label={'MSOL_SYNC_SOURCE'}
                        options={syncSources}
                        itemTemplate={getSyncSourceTemplate}
                    />
                    {officeConfig.selectedSyncSource === 'PrincipalName' ? (
                        <div className='msol__note'>{translation('MSOL_PRINCIPAL_NAME_NOTE')}</div>
                    ) : null}
                </Box>
                <Box mb={2}>
                    <PasslySelect
                        fullWidth
                        name='selectedLicense'
                        value={officeConfig.selectedLicense}
                        id='selectedLicense-input'
                        onChange={handleConfigUpdate}
                        label={'MSOL_LICENSE_BEHAVIOR'}
                        options={officeConfig.licenses}
                    />
                </Box>
                {(officeConfig?.selectedLicense !== 'none' && officeConfig.managedSetup.graphApiEnabled) ||
                (officeConfig?.selectedLicense !== 'none' && officeConfig.federationType === MODULE_NAME.graphModule) ? (
                    <Box mb={2}>
                        <PasslySelect
                            fullWidth
                            name='usageLocation'
                            value={officeConfig.usageLocation}
                            id='usageLocation-input'
                            onChange={handleConfigUpdate}
                            label={'USAGE_LOCATION'}
                            options={officeConfig.locations}
                            optionLabel='name'
                            optionValue='id'
                        />
                    </Box>
                ) : null}
            </Grid>
            <Grid item lg={5} sm={7}>
                <Typography variant='h6'>{translation('MSOL_CONFIGURE_OFFICE365_IMPORT')}</Typography>
                <PasslyCheckbox
                    name='importMsolEnabled'
                    checked={officeConfig.importMsolEnabled}
                    onChange={handleCheckboxChange}
                    label={translation('MSOL_ENABLE_OFFICE365_IMPORT')}
                />
                {officeConfig.importMsolEnabled && (
                    <>
                        <PasslyCheckbox
                            name='importMsolUsersEnabled'
                            checked={officeConfig.importMsolUsersEnabled}
                            onChange={handleCheckboxChange}
                            label={translation('MSOL_ENABLE_OFFICE365_IMPORT_USERS')}
                        />
                        <PasslyCheckbox
                            name='importMsolGroupsEnabled'
                            checked={officeConfig.importMsolGroupsEnabled}
                            onChange={handleCheckboxChange}
                            label={translation('MSOL_ENABLE_OFFICE365_IMPORT_GROUPS')}
                        />
                        {groups.length > 0 && (
                            <Box mb={2}>
                                <PasslyAutoComplete
                                    className='passwordPolicy'
                                    items={groups}
                                    onChange={handleAutocompleteChange}
                                    id='groupSelect'
                                    name='passwordPolicy'
                                    label={translation('MSOL_SELECT_GROUP_TO_IMPORT_USERS')}
                                    field='Name'
                                    dropdown={true}
                                />
                            </Box>
                        )}
                        <Box mb={2}>
                            <PasslySelect
                                fullWidth
                                required
                                name='importedUsersProvisioningPolicyId'
                                defaultValue={provisioningPolicies?.length > 0 ? provisioningPolicies[0].Id : ''}
                                value={officeConfig.importedUsersProvisioningPolicyId}
                                id='importedUsersProvisioningPolicyId-input'
                                onChange={handleConfigUpdate}
                                label={'PROVISIONING_POLICY'}
                                options={provisioningPolicies}
                                optionLabel='DisplayName'
                                optionValue='Id'
                            />
                        </Box>
                    </>
                )}
            </Grid>
        </Grid>
    );
};
